import { QueryParamsObject } from "@/api/types";

export const normalizeDoubleSpaces = (text: string) => {
  // remove double spaces and replace with a single space
  return text.replace(/ {2,}/g, " ").replace(/(&nbsp;){2,}/g, "&nbsp;");
};

export const removeSpacesAtEnd = (text: string) => {
  // remove spaces at the end of the string
  let result = text.replace(/[ \t]+$/gm, "");
  // replace "&nbsp;</p>" if it is at end of the string
  result = result.replace(/(&nbsp;)+<\/p>$/i, "</p>");

  return result;
};

export const removeSpacesAtStart = (text: string) => {
  // remove spaces at the start of the string
  return text.replace(/^[ \t]+/gm, "");
};

export const removeDotsAtEnd = (text: string) => {
  //check if string ends with triple dots
  if (!text.endsWith("...")) {
    //remove all dots and spaces at the end of the string
    return text.replace(/(\s+|\.+)+$/, "");
  }
  return text;
};

export const sortBy = (
  arr: any[] | undefined,
  key: string | undefined,
  caseSensitive: boolean = false,
) => {
  if (!Array.isArray(arr)) {
    return [];
  }

  if (typeof key !== "string" || key.trim() === "") {
    return arr;
  }

  return arr.slice().sort((a, b) => {
    if (!(key in a) || !(key in b)) {
      return 0;
    }

    let aVal = String(a[key]);
    let bVal = String(b[key]);

    if (!caseSensitive) {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    return aVal.localeCompare(bVal, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
};

export const cloneDeep = (value: any) => {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    const copy: any[] = [];
    for (let i = 0; i < value.length; i++) {
      copy[i] = cloneDeep(value[i]);
    }
    return copy;
  }

  if (value instanceof Object) {
    const copy: { [key: string]: any } = {};
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        copy[key] = cloneDeep((value as { [key: string]: any })[key]);
      }
    }
    return copy;
  }

  throw new Error("Unable to copy value! Its type isn't supported.");
};

export const deepEqual = (obj1: any, obj2: any) => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

export const deepCompareArrays = (arr1: any[], arr2: any[]): boolean => {
  if (arr1.length !== arr2.length) {
    let isEmpty = false;
    const count = arr2.filter((item) => item.id === 0).length;
    if (count === 1) {
      for (const element of arr2) {
        if (element.id === 0 && element.name === "") {
          isEmpty = true;
        }
      }
    }
    return !!isEmpty;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) return false;
  }

  return true;
};

export const writeToLocalStorage = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error("Error writing to localStorage", error);
  }
};

export const readFromLocalStorage = <T>(key: string): T | null => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue === null) {
      return null;
    }
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    console.error("Error reading from localStorage", error);
    return null;
  }
};

export const removeFromLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing from localStorage", error);
  }
};

/**
 * Converts an object of filters and sorting into a URL query string.
 * @param {Object} params - The parameters object.
 * @param {Object} params.filters - An object representing filters (key-value pairs).
 * @param {Object} params.sort - An object with 'field' (string) and 'direction' ('asc' or 'desc').
 * @returns {string} The query string for the URL.
 */
export const buildFilterString = (params: QueryParamsObject): string => {
  const queryParts = [];

  // Add filters
  if (params.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (value === "") return;
      if (Array.isArray(value)) {
        // Handle array values for logical OR
        queryParts.push(`${key}=${value.join(",")}`);
      } else {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    });
  }

  // Add sorting
  if (params.sort) {
    const { key } = params.sort[0];
    let { order } = params.sort[0];
    if (key) {
      if (!order) order = "desc";
      queryParts.push(`sortKey=${key},${order}`);
    }
  }

  if (params.limit) {
    queryParts.push(`limit=${params.limit}`);
  }

  if (params.page) {
    queryParts.push(`page=${params.page}`);
  }

  // Join all parts with '&'
  return queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
};
